/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Download,
  FilePdf,
  Files,
  FileText,
  Paperclip,
  Trash,
} from '@phosphor-icons/react'
import Spinner from 'components/Spinner'
import { restartAuditLog } from 'pages/AuditLog/api'
import {
  invalidateLoadCustomers,
  useLoadCustomer,
} from 'queries/customers/useLoadCustomer'
import React, { useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Dropdown from 'ui/atoms/Dropdown'
import Flex from 'ui/atoms/Flex'
import StatusBall from 'ui/atoms/StatusBall'
import Tooltip from 'ui/atoms/Tooltip'
import Icons from 'ui/Icons'
import { isFilledArray, momentLocal } from 'utils/helpers'

import useIntegration from '@/hooks/useIntegration'
import { useDeleteCustomerFileAttachment } from '@/queries/customers/useDeleteCustomerFileAttachment'
import { Form } from '@/types/form'
import { Packet } from '@/types/packet'
import { PdfDocumentApiResponse } from '@/types/pdf-document-api-response'
import { downloadFromURL } from '@/utils/downloadManager'

function HeaderItem({ children }: { children?: React.ReactNode }) {
  return (
    <div className="table-cell border-b border-gray-200 p-3 text-xs font-medium uppercase leading-4 tracking-wider text-gray-500">
      {children}
    </div>
  )
}

function RowItem({
  light,
  children,
}: {
  light?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`table-cell border-b border-gray-200 px-3 py-4 text-sm font-medium leading-5 text-gray-${
        light ? '500' : '900'
      } max-w-xs`}
    >
      {children}
    </div>
  )
}

export type TimelineRowItem = {
  id: string
  document?: PdfDocumentApiResponse | null
  form?: Form | null
  is_submitted: boolean
  submitted_at: string
  integration_logs: {
    created_at: string
    status: 'succeed' | 'in_progress' | 'failed'
    uid: string
  }[]
  packet?: Packet | null
  created_at: string
  file_name?: string
  file?: string | null
}

function ResponseRow({
  response,
  onClick,
  clientId,
}: {
  response: TimelineRowItem
  onClick?: () => void
  clientId: string
}) {
  const { integration } = useIntegration()
  const [loading, setLoading] = React.useState(false)
  const { deleteCustomerFileAttachment } = useDeleteCustomerFileAttachment()

  const {
    id,
    document,
    form,
    packet,
    is_submitted,
    submitted_at,
    integration_logs,
    file_name,
    file,
  } = response
  const isPacket = !!packet?.id

  const resourceName = React.useMemo(() => {
    if (file_name) return file_name

    if (isPacket) return packet.name

    if (document) return document.name

    return form?.name ?? ''
  }, [isPacket, packet?.name, document, file_name, form?.name])

  const integration_log = integration_logs?.[0]

  const { integration_label, integration_status } = React.useMemo(() => {
    if (integration_log) {
      switch (integration_log.status) {
        case 'succeed':
          return { integration_label: 'Success', integration_status: 'success' }
        case 'in_progress':
          return {
            integration_label: 'In Progress',
            integration_status: 'warning',
          }
        default:
          return { integration_label: 'Failed', integration_status: 'error' }
      }
    }
    return { integration_label: 'N/A', integration_status: 'default' }
  }, [integration_log])

  const restartDisabled = React.useMemo(() => {
    if (!integration_log) return true
    if (!integration_log.created_at) return false
    const created_at = new Date(integration_log?.created_at + 'Z')
    const now = new Date()
    const diff = now.getTime() - created_at.getTime()
    const diffInMinutes = diff / 1000 / 60
    return diffInMinutes < 1.0
  }, [integration_log])

  const ResponseLink = useCallback(
    ({ children }) => {
      let path = `/response/${id}?response_type=${isPacket ? 'packet' : 'form'}`
      if (document) {
        path = `/response/${id}`
      }

      if (file) {
        return (
          <div className="table-row cursor-pointer hover:bg-gray-100 hover:no-underline">
            {children}
          </div>
        )
      }

      return (
        <Link
          className="table-row cursor-pointer hover:bg-gray-100 hover:no-underline"
          to={path}
        >
          {children}
        </Link>
      )
    },
    [document, file, id, isPacket]
  )

  const getIcon = () => {
    if (file_name) return <Paperclip size={16} />
    if (isPacket) return <Files size={16} />
    if (document) return <FilePdf size={16} />
    if (form) return <FileText size={16} />
    return null
  }

  return (
    <ResponseLink>
      <RowItem>
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0">{getIcon()}</div>
          <div className="truncate">{resourceName}</div>
        </div>
      </RowItem>
      <RowItem light>
        {file_name ? '-' : is_submitted ? 'Completed' : 'Pending'}
      </RowItem>
      <RowItem light>
        {submitted_at ? momentLocal(submitted_at).format('L') : '-'}
      </RowItem>
      {!!integration && (
        <RowItem light>
          <div className="tooltipBoundary flex w-full items-center justify-center">
            <Tooltip arrow label={integration_label} placement="top">
              <button className="flex items-center justify-center border-none focus:outline-none">
                <StatusBall
                  hidden={integration_status === 'default'}
                  size="small"
                  // @ts-ignore
                  status={integration_status}
                />
              </button>
            </Tooltip>
          </div>
        </RowItem>
      )}
      {/* @ts-ignore */}
      <RowItem light className="relative">
        <Dropdown
          disabled={restartDisabled && !file_name}
          items={[
            ...(!file_name
              ? [
                  {
                    key: 'restart-integration',
                    label: (
                      <button
                        className="block w-full px-4 py-2 text-center text-sm leading-5 text-txt-emphasis transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none"
                        onClick={async (e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setLoading(true)
                          await restartAuditLog([integration_log?.uid])
                          await invalidateLoadCustomers(clientId)
                          setLoading(false)
                        }}
                      >
                        {loading ? 'Loading...' : 'Retry Integration'}
                      </button>
                    ),
                  },
                ]
              : []),
            ...(file_name
              ? [
                  {
                    key: 'download-file',
                    label: (
                      <button
                        className="flex w-full gap-2 px-4 py-2 text-center text-sm leading-5 text-txt-emphasis transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none"
                        onClick={async (e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          if (file) {
                            try {
                              setLoading(true)
                              await downloadFromURL(file, file_name)
                            } catch (error) {
                              toast.error('Failed to download file')
                            } finally {
                              setLoading(false)
                            }
                          }
                        }}
                      >
                        <Download size={16} />
                        {loading ? 'Downloading...' : 'Download File'}
                      </button>
                    ),
                  },
                  {
                    key: 'delete-file',
                    label: (
                      <button
                        className="flex w-full gap-2 px-4 py-2 text-center text-sm leading-5 text-txt-emphasis transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none"
                        onClick={async (e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          await deleteCustomerFileAttachment({
                            id: Number(id),
                            customerId: clientId,
                          })
                        }}
                      >
                        <Trash size={16} />
                        Delete File
                      </button>
                    ),
                  },
                ]
              : []),
          ]}
        >
          <button
            className={`m-auto flex items-center ${
              restartDisabled && !file_name
                ? 'text-gray-200'
                : 'text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none'
            }`}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            {/* @ts-ignore */}
            <Icons.ChevronDown inheritColor className="mr-2.5" size={4} />
          </button>
        </Dropdown>
      </RowItem>
    </ResponseLink>
  )
}

function sortClientResponses(
  a: {
    is_submitted?: boolean
    submitted_at?: string
    created_at: string
    file_name?: string
  },
  b: {
    is_submitted?: boolean
    submitted_at?: string
    created_at: string
    file_name?: string
  }
) {
  if (!a.file_name && !b.file_name) {
    // Prioritize submitted over non-submitted if both are not files
    if (a.is_submitted && !b.is_submitted) return -1
    if (!a.is_submitted && b.is_submitted) return 1
  }

  const dateA = a.submitted_at || a.created_at
  const dateB = b.submitted_at || b.created_at
  if (dateA > dateB) return -1
  if (dateA < dateB) return 1

  return 0
}

function ClientTimelineTable({ loading }: { loading: boolean }) {
  const { clientId } = useParams<{ clientId: string }>()
  const {
    data: clientInDetail,
    isRefetching,
    isLoading,
  } = useLoadCustomer(clientId, {
    enabled: !!clientId,
  })
  const [clientResponses, setClientResponses] = React.useState<
    TimelineRowItem[]
  >([])

  const { integration } = useIntegration()

  React.useEffect(() => {
    function getClientResponses() {
      const mergedResponses = [
        ...(clientInDetail?.responses ?? []),
        ...(clientInDetail?.packet_responses ?? []),
        ...(clientInDetail?.customer_file_attachments ?? []),
      ]
      const orderedResponses = isFilledArray(mergedResponses)
        ? // @ts-ignore
          mergedResponses.sort(sortClientResponses)
        : []
      // @ts-ignore
      setClientResponses(orderedResponses)
    }

    getClientResponses()
  }, [clientInDetail])

  if (loading || isRefetching || isLoading) {
    return (
      <div className="mb-10 flex w-full items-center justify-center rounded-bl rounded-br rounded-tr border bg-gray-50 p-3 text-center text-xs font-medium uppercase leading-4 tracking-wider text-gray-500">
        <Spinner color="onLight" size={16} />
        <span className="ml-2">Loading customer responses</span>
      </div>
    )
  }

  return isFilledArray(clientResponses) ? (
    <div className="mb-10 table w-full rounded-bl rounded-br rounded-tr border bg-white">
      <div className="table-header-group">
        <div className="table-row bg-gray-50">
          <HeaderItem>Title</HeaderItem>
          <HeaderItem>Status</HeaderItem>
          <HeaderItem>Date Completed</HeaderItem>
          {integration ? (
            <>
              <HeaderItem>
                <Flex justify="center">Integration</Flex>
              </HeaderItem>
              <HeaderItem />
            </>
          ) : (
            <HeaderItem />
          )}
        </div>
      </div>
      <div className="table-row-group">
        {clientResponses.map((r) => (
          <ResponseRow clientId={clientId} key={r.id} response={r} />
        ))}
      </div>
    </div>
  ) : (
    <div className="mb-10 flex w-full items-center justify-center rounded-bl rounded-br rounded-tr border bg-gray-50 p-3 text-center text-xs font-medium uppercase leading-4 tracking-wider text-gray-500">
      No response available
    </div>
  )
}

export default ClientTimelineTable
